import * as React from 'react'
import {ReactElement} from 'react'
import {Box} from '@mui/material'
import {Colors, FontSize} from '../../../../styledComponents/Constants'
import PPRadioGroupForResident, {
    ResidentRadioButtonProps,
} from '../../../../components/shared/forms/resident/PPRadioGroupForResident'
import {FullWidthColumnFlexContainer, LeftRowFlexContainer} from '../../../../styledComponents/FlexComponents'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {RESIDENCE_STYLE} from '../../../../CodeMaster'
import {Nullable} from '../../../../app/TypeHelpers'
import {
    ApplyButton,
    BoldText,
    ItemHeading,
    ListGridContainer,
    NumberListContainer,
    NumberListItem,
    PageTitle,
    PledgeArea,
    PolicyCheckBoxStyled,
    WideWhiteArea,
} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {NatContactAddressOnEmergencyInfo} from './NatResidentApplicationInterface'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import moment from 'moment'
import {SessionStorageHelper} from '../../../../SessionStorageHelper'
import {Location} from 'react-router-dom'

interface Props {
    applicationRepo: NatApplicationRepo
    condominiumCd: string
    applyId?: string
    location: Location
}

interface State {
    residentStyle: Nullable<string>
    contactAddressOnEmergencyInfo: Nullable<NatContactAddressOnEmergencyInfo>
    policyChecked: boolean
    applyButtonDisabled: boolean
    isLoadCompleted: boolean
}

class NatResidentContactAddressOnEmergencyAgreementPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            residentStyle: null,
            contactAddressOnEmergencyInfo: null,
            policyChecked: false,
            applyButtonDisabled: true,
            isLoadCompleted: false,
        }

        this.handleResidentStyleChange = this.handleResidentStyleChange.bind(this)
        this.onChangePolicyChecked = this.onChangePolicyChecked.bind(this)
        this.onClickApplyButton = this.onClickApplyButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {condominiumCd, applyId, applicationRepo, eventEmitter} = this.props

        if (applyId) {
            await this.props.eventEmitter.showLoading()

            try {
                const contactAddressOnEmergencyDetail = await this.props.asyncCallFunction(
                    applicationRepo.getContactAddressOnEmergencyForResident.bind(
                        applicationRepo, condominiumCd, applyId,
                    ),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (contactAddressOnEmergencyDetail?.details) {
                    const response = contactAddressOnEmergencyDetail.details

                    const defaultFamilyStructureList = response.familyStructure.map((list, index) => ({
                        index: index,
                        relationship: list.relationship,
                        familyName: list.familyName,
                        firstName: list.firstName,
                        familyNameKana: list.familyNameKana,
                        firstNameKana: list.firstNameKana,
                        birthDate: list.birthDate ?? '',
                        birthYear: list.birthDate ? moment(list.birthDate).format('YYYY') : '',
                        birthMonth: list.birthDate ? moment(list.birthDate).format('MM') : '',
                        birthDay: list.birthDate ? moment(list.birthDate).format('DD') : '',
                        workAddress: list.workAddress,
                    }))

                    const defaultContactAddressOnEmergencyInfo = {
                        applicantFamilyName: SessionStorageHelper.getStringItemWithDefault('loginFamilyName', ''),
                        applicantFirstName: SessionStorageHelper.getStringItemWithDefault('loginFirstName', ''),
                        occupancyDate: response.occupancyDate,
                        contactAddressOnEmergency: response.contactAddressOnEmergency,
                        absenceContactAddress: response.absenceContactAddress,
                        rentAdminContactAddressFlag: response.rentAdminContactAddressFlag,
                        rentAdminContactAddress: response.rentAdminContactAddress,
                        familyStructureList: defaultFamilyStructureList,
                    }

                    this.setState({
                        residentStyle: response.residenceStyle,
                        contactAddressOnEmergencyInfo: defaultContactAddressOnEmergencyInfo,
                        applyButtonDisabled: false,
                    })
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }

        this.setState({
            isLoadCompleted: true,
        })
    }

    handleResidentStyleChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            residentStyle: event.target.value,
            policyChecked: false,
        }, () => {
            this.updateApplyButtonDisabled()
        })
    }

    onChangePolicyChecked(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            policyChecked: event.target.checked,
        }, () => {
            this.updateApplyButtonDisabled()
        })
    }

    updateApplyButtonDisabled(): void {
        const {residentStyle, policyChecked} = this.state

        if (residentStyle === RESIDENCE_STYLE.UNIT_OWNER.value) {
            this.setState({applyButtonDisabled: false})
        } else if (policyChecked) {
            this.setState({applyButtonDisabled: false})
        } else {
            this.setState({applyButtonDisabled: true})
        }
    }

    onClickApplyButton(): void {
        this.props.navigate(
            `/resident/condominiums/${this.props.condominiumCd}/applications/contactAddressOnEmergencies/input`,
            {
                state: {
                    applyInfo: this.state.contactAddressOnEmergencyInfo,
                    applyId: this.props.applyId,
                    residenceStyle: this.state.residentStyle,
                },
            },
        )
    }

    renderPolicy(): Nullable<ReactElement> {
        const {applyId} = this.props
        const {residentStyle} = this.state

        if ((residentStyle === null || residentStyle === RESIDENCE_STYLE.UNIT_OWNER.value) || applyId) {
            return null
        }

        const policyList: string[] = []
        if (residentStyle === null || residentStyle === RESIDENCE_STYLE.APPROPRIATOR_AND_TENANT.value) {
            policyList.push('管理規約に基づき、規約並びに諸規則に定める事項及び総会の決議を遵守することを誓約いたします。')
            policyList.push('管理規約に基づき、自らが反社会的勢力でないことを表明し、その後も反社会的勢力にならないことを確約いたします。')
        } else {
            policyList.push('管理規約に基づき、占有者に規約並びに諸規則に定める事項及び総会の決議を遵守させることを誓約いたします。')
            policyList.push('管理規約に基づき、自らが反社会的勢力でないことを表明し、その後も反社会的勢力にならないことを確約いたします。')
        }

        return (<>
            <Box height={'15px'}/>
            <PledgeArea fontSize={FontSize.Middle}>
                <Box p={'10px'}>
                    <BoldText fontColor={Colors.AccentDark}>誓約文</BoldText>
                    <Box height={'5px'}/>
                    <NumberListContainer>
                        {policyList.map((policy, index) => {
                            return (<NumberListItem key={index}>{policy}</NumberListItem>)
                        })}
                    </NumberListContainer>
                </Box>
            </PledgeArea>
            <Box height={'20px'}/>
            <FullWidthColumnFlexContainer>
                <PolicyCheckBoxStyled>
                    <LeftRowFlexContainer>
                        <input
                            type="checkbox"
                            checked={this.state.policyChecked}
                            onChange={this.onChangePolicyChecked}
                        />
                        <BlockSpacer width={'10px'}/>
                        誓約文に同意する
                    </LeftRowFlexContainer>
                </PolicyCheckBoxStyled>
            </FullWidthColumnFlexContainer>
        </>)
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        const residentStyles: ResidentRadioButtonProps[] = [{
            label: RESIDENCE_STYLE.UNIT_OWNER.label,
            value: RESIDENCE_STYLE.UNIT_OWNER.value,
            description: '対象の部屋を所有されている方、かつ対象の部屋にお住まいの方',
        }, {
            label: RESIDENCE_STYLE.APPROPRIATOR_AND_TENANT.label,
            value: RESIDENCE_STYLE.APPROPRIATOR_AND_TENANT.value,
            description: '対象の部屋を賃借されている方',
        }, {
            label: RESIDENCE_STYLE.EXTERNAL_UNIT_OWNER.label,
            value: RESIDENCE_STYLE.EXTERNAL_UNIT_OWNER.value,
            description: '対象の部屋を所有されている方、かつ対象の部屋にお住まいでない方',
        },
        ]

        const disabledDataList: ResidentRadioButtonProps[] = this.props.applyId
            ? residentStyles.filter((list) => list.value !== this.state.residentStyle) : []

        return (
            <>
                <PlainBackLink
                    to={this.props.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/contactAddressOnEmergencies/${this.props.applyId}`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    緊急時連絡表
                </PageTitle>
                <Box height={'20px'}/>
                <Box>
                    緊急事態が発生した際、管理組合または管理会社から連絡させていただくための連絡先を登録します。<br/>
                    <Box height={'10px'}/>
                    <ListGridContainer fontSize={FontSize.Middle}>
                        <div>・</div>
                        <div>未提出や不備などに起因する人的/物的損害は管理受託者の免責事項となります。</div>
                        <div>・</div>
                        <div>国内の連絡先をご入力ください。</div>
                        <div>・</div>
                        <div>
                            お手元に<BoldText>本人または家族の勤務先住所や電話番号が確認できるもの</BoldText>をご用意いただくと、入力をスムーズに進めることができます。
                        </div>
                    </ListGridContainer>
                </Box>
                <Box height={'15px'}/>
                <ItemHeading>
                    居住形態
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    {
                        !this.props.applyId &&
                        <Box mb={'10px'}>
                            ３つの居住形態から当てはまるものを選択
                        </Box>
                    }
                    <PPRadioGroupForResident
                        data={residentStyles}
                        selectValue={this.state.residentStyle ?? ''}
                        disabledDataList={disabledDataList}
                        onChange={this.handleResidentStyleChange}/>
                    <Box height={'20px'}/>
                    {
                        this.props.applyId &&
                        <Box fontSize={FontSize.Middle}>
                            ※再申請では、居住形態を変更できません。<br/>
                            居住形態を変更する場合は、届出・申請メニューから新規に申請を開始してください。
                            <Box height={'20px'}/>
                        </Box>
                    }
                </WideWhiteArea>
                {this.renderPolicy()}
                <ApplyButton
                    disabled={this.state.applyButtonDisabled}
                    onClick={this.onClickApplyButton}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            申請を開始する
                        </div>
                    </FullWidthColumnFlexContainer>
                </ApplyButton>
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentContactAddressOnEmergencyAgreementPage)
